/**
 * DoSearchModule — admin Delivery Order Search & Detail (ODE-P07-T03).
 *
 * Renders the bounded DO search (REQ-044 status + the five REQ-074 completion
 * states) and the read-only detail projection (DO master + the ODE-P06-T06
 * server-authoritative completion + checkpoint/audit summary) with the full
 * REQ-090 state set:
 *
 *   pending       filters not yet submitted (nothing fetched)
 *   loading       search fetch in flight
 *   empty         200 with zero rows — explicit "no match", never fabricated
 *   ready         server page rendered (REQ-044 + REQ-074 columns)
 *   unauthorized  401 — session missing/expired
 *   blocked       403 — role denied (server-enforced; the mirror is UX only)
 *   error         400/503/network — retryable
 *
 * Completion is never computed here: the state text and the REQ-073 authority
 * marker come from the server projection verbatim (a device-declared finish is
 * labelled "belum selesai" and is never rendered as COMPLETE). All output
 * carries stable `data-testid`s for deterministic tests, and status is never
 * encoded by color alone.
 *
 * Traces to: ODE-PRD-REQ-044, ODE-PRD-REQ-054, ODE-PRD-REQ-073,
 *            ODE-PRD-REQ-074, ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { AdminDoDetail, AdminDoSearchRow } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  useDoSearch,
  type DoDetailState,
  type DoSearchListState,
  type UseDoSearchOptions,
} from './useDoSearch.js';

/** Status filter options (REQ-044 lifecycle + REQ-074 completion states). */
const STATUS_OPTIONS: readonly string[] = [
  'ASSIGNED',
  'READY',
  'ACTIVE',
  'OPERATIONALLY_FINISHED',
  'PENDING_SYNC',
  'COMPLETION_PENDING',
  'COMPLETE',
  'COMPLETION_ERROR',
];

/** Plain-Indonesian labels for the five REQ-074 completion states. */
const COMPLETION_LABELS: Readonly<Record<string, string>> = {
  OPERATIONALLY_FINISHED: 'Selesai operasional (belum tervalidasi server)',
  PENDING_SYNC: 'Menunggu sinkronisasi bukti',
  COMPLETION_PENDING: 'Menunggu validasi penyelesaian',
  COMPLETE: 'Selesai (divalidasi server)',
  COMPLETION_ERROR: 'Gagal sinkronisasi penyelesaian',
};

export interface DoSearchSeams {
  readonly search?: UseDoSearchOptions['search'];
  readonly detail?: UseDoSearchOptions['detail'];
}

export function DoSearchModule({ seams = {} }: { readonly seams?: DoSearchSeams } = {}): JSX.Element {
  const { session } = useAuth();
  const doSearch = useDoSearch({
    token: session?.token ?? null,
    ...(seams.search === undefined ? {} : { search: seams.search }),
    ...(seams.detail === undefined ? {} : { detail: seams.detail }),
  });
  const { form, setForm, state, detailState } = doSearch;

  return (
    <section aria-labelledby="do-search-title" className="module">
      <h2 id="do-search-title">Pencarian Delivery Order</h2>
      <p className="module-description">
        Cari Delivery Order berdasarkan nomor, status (termasuk status penyelesaian
        REQ-074), rentang tanggal, atau driver/kendaraan — lalu buka detail
        baca-saja (REQ-044/054/073/074).
      </p>

      <form
        className="do-search-form"
        data-testid="do-search-form"
        onSubmit={(event) => {
          event.preventDefault();
          doSearch.submit();
        }}
      >
        <label>
          Nomor DO
          <input
            data-testid="filter-do-number"
            value={form.doNumber}
            onChange={(event) => setForm({ doNumber: event.target.value })}
            placeholder="mis. DO-7001"
          />
        </label>
        <label>
          Status
          <select
            data-testid="filter-status"
            value={form.status}
            onChange={(event) => setForm({ status: event.target.value })}
          >
            <option value="">Semua status</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label>
          Dari tanggal
          <input
            data-testid="filter-from"
            value={form.from}
            onChange={(event) => setForm({ from: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Sampai tanggal
          <input
            data-testid="filter-to"
            value={form.to}
            onChange={(event) => setForm({ to: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Driver
          <input
            data-testid="filter-driver"
            value={form.driverRef}
            onChange={(event) => setForm({ driverRef: event.target.value })}
          />
        </label>
        <label>
          Kendaraan
          <input
            data-testid="filter-vehicle"
            value={form.vehicleRef}
            onChange={(event) => setForm({ vehicleRef: event.target.value })}
          />
        </label>
        <label>
          Batas baris
          <input
            data-testid="filter-limit"
            value={form.limit}
            onChange={(event) => setForm({ limit: event.target.value })}
            placeholder="20"
          />
        </label>
        <div className="do-search-actions">
          <button type="submit" data-testid="do-search-submit">
            Cari
          </button>
          <button type="button" data-testid="do-search-reset" onClick={doSearch.reset}>
            Atur ulang
          </button>
        </div>
      </form>

      {state.kind === 'pending' ? (
        <StateView state={{ kind: 'pending' }} />
      ) : state.kind === 'ready' ? (
        <SearchResults
          state={state}
          onOpenDetail={doSearch.openDetail}
          onLoadMore={doSearch.loadMore}
        />
      ) : (
        <StateView state={toModuleState(state)} onRetry={doSearch.submit} />
      )}

      <DetailPanel state={detailState} onClose={doSearch.closeDetail} />
    </section>
  );
}

/** Maps the search list state to the shared REQ-090 renderer. */
function toModuleState(state: DoSearchListState): ModuleState {
  switch (state.kind) {
    case 'pending':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      return { kind: 'empty', payload: state.page };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
  }
}

function SearchResults({
  state,
  onOpenDetail,
  onLoadMore,
}: {
  readonly state: Extract<DoSearchListState, { kind: 'ready' }>;
  readonly onOpenDetail: (doId: string) => void;
  readonly onLoadMore: () => void;
}): JSX.Element {
  const { page } = state;
  if (page.rows.length === 0) {
    return (
      <div data-testid="do-search-empty" className="state state-empty">
        <h3>Belum ada data</h3>
        <p>Tidak ada Delivery Order yang cocok dengan filter ini.</p>
      </div>
    );
  }
  return (
    <div data-testid="do-search-results">
      <table className="overview-table">
        <thead>
          <tr>
            <th scope="col">Nomor DO</th>
            <th scope="col">Status (REQ-044)</th>
            <th scope="col">Penyelesaian (REQ-074)</th>
            <th scope="col">Driver</th>
            <th scope="col">Kendaraan</th>
            <th scope="col">Diperbarui</th>
            <th scope="col">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {page.rows.map((row) => (
            <DoSearchRowView key={row.deliveryOrderId} row={row} onOpenDetail={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <p className="do-search-asof" data-testid="do-search-asof">
        {page.rows.length} baris · per {new Date(page.asOfEpochMs).toISOString()}
      </p>
      {page.hasMore && (
        <button type="button" data-testid="do-search-load-more" onClick={onLoadMore} disabled={state.loadingMore}>
          {state.loadingMore ? 'Memuat…' : 'Muat lagi'}
        </button>
      )}
    </div>
  );
}

function DoSearchRowView({
  row,
  onOpenDetail,
}: {
  readonly row: AdminDoSearchRow;
  readonly onOpenDetail: (doId: string) => void;
}): JSX.Element {
  return (
    <tr data-testid={`do-row-${row.deliveryOrderId}`}>
      <td>{row.doNumber}</td>
      <td data-testid={`do-status-${row.deliveryOrderId}`}>{row.status}</td>
      <td data-testid={`do-completion-${row.deliveryOrderId}`}>
        {COMPLETION_LABELS[row.completionState] ?? row.completionState}{' '}
        <span
          className={row.completionAuthoritative ? 'badge badge-fresh' : 'badge badge-stale'}
          data-testid={`do-completion-authority-${row.deliveryOrderId}`}
        >
          {row.completionAuthoritative ? 'otoritatif server' : 'belum otoritatif'}
        </span>
      </td>
      <td>{row.driverRef ?? '—'}</td>
      <td>{row.vehicleRef ?? '—'}</td>
      <td>{row.updatedAt}</td>
      <td>
        <button
          type="button"
          data-testid={`do-detail-${row.deliveryOrderId}`}
          onClick={() => onOpenDetail(row.deliveryOrderId)}
        >
          Detail
        </button>
      </td>
    </tr>
  );
}

function DetailPanel({
  state,
  onClose,
}: {
  readonly state: DoDetailState;
  readonly onClose: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;

  return (
    <section aria-label="Detail Delivery Order" data-testid="do-detail-panel" className="do-detail">
      <div className="do-detail-head">
        <h3>Detail Delivery Order</h3>
        <button type="button" data-testid="do-detail-close" onClick={onClose}>
          Tutup
        </button>
      </div>
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'not_found' && (
        <div role="alert" data-testid="do-detail-not-found" className="state state-error">
          <h3>Tidak ditemukan</h3>
          <p>Delivery Order tidak dikenal.</p>
        </div>
      )}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' && <DetailBody detail={state.detail} />}
    </section>
  );
}

function DetailBody({ detail }: { readonly detail: AdminDoDetail }): JSX.Element {
  return (
    <div data-testid="do-detail-ready">
      <dl className="do-detail-grid">
        <dt>Nomor DO</dt>
        <dd data-testid="detail-do-number">{detail.deliveryOrder.doNumber}</dd>
        <dt>Status (REQ-044)</dt>
        <dd data-testid="detail-status">{detail.deliveryOrder.status}</dd>
        <dt>Revisi</dt>
        <dd>{detail.deliveryOrder.revision}</dd>
        <dt>Driver</dt>
        <dd>{detail.deliveryOrder.driverRef ?? '—'}</dd>
        <dt>Kendaraan</dt>
        <dd>{detail.deliveryOrder.vehicleRef ?? '—'}</dd>
      </dl>

      <section aria-label="Status penyelesaian" data-testid="detail-completion">
        <h4>Penyelesaian (REQ-073/074)</h4>
        <p data-testid="detail-completion-state">
          {COMPLETION_LABELS[detail.completion.state] ?? detail.completion.state}
        </p>
        <p data-testid="detail-completion-authority">
          {detail.completion.authoritative
            ? `Divalidasi server${detail.completion.validatedBy === null ? '' : ` oleh ${detail.completion.validatedBy}`}`
            : 'Belum divalidasi server — perangkat tidak otoritatif (REQ-073).'}
        </p>
        {detail.completion.reasonCodes.length > 0 && (
          <p data-testid="detail-completion-reasons">
            Alasan: {detail.completion.reasonCodes.join(', ')}
          </p>
        )}
      </section>

      <section aria-label="Ringkasan checkpoint" data-testid="detail-checkpoints">
        <h4>Checkpoint</h4>
        <p>
          {detail.checkpoints.completed} / {detail.checkpoints.total} selesai
          {detail.checkpoints.lastCheckpointId === null
            ? ''
            : ` · terakhir ${detail.checkpoints.lastCheckpointId}`}
        </p>
      </section>

      <section aria-label="Ringkasan audit" data-testid="detail-audit">
        <h4>Audit (REQ-076)</h4>
        <p>
          {detail.audit.acceptedCompletionEvents} peristiwa penyelesaian tercatat
          {detail.audit.lastActorType === null ? '' : ` · aktor terakhir ${detail.audit.lastActorType}`}
        </p>
      </section>
    </div>
  );
}
